import axios from "axios";
import { Token } from "../utils/localStorage.util";
import appConfig from "../config.js";
import { emitSessionExpired } from "../utils/sessionEvents.js";

const apiPlanejamentoTributario = axios.create({
  baseURL: appConfig.REACT_APP_HOST_API_PLANEJAMENTO_TRIBUTARIO,
});

const getAuthHeaders = () => {
  const token = Token.getToken();
  return token ? { Authorization: token } : {};
};

apiPlanejamentoTributario.interceptors.request.use(
  async (axiosConfig) => {
    const token = Token.getToken();
    if (token) {
      axiosConfig.headers.Authorization = token;
    }

    if (!axiosConfig.headers["Content-Type"]) {
      axiosConfig.headers["Content-Type"] = "application/json";
    }

    return axiosConfig;
  },
  (error) => Promise.reject(error)
);

apiPlanejamentoTributario.interceptors.response.use(
  (response) => {
    return response.data;
  },

  async function (error) {
    if (error?.response?.status === 401) {
      emitSessionExpired();
    }
    return Promise.reject(error);
  }
);

export function cadastrarParceiroDetalhado(body) {
  return apiPlanejamentoTributario.post(`/parceiros`, body);
}

export function buscarServicos({ incluirInativos = false } = {}) {
  const query = incluirInativos ? "?incluirInativos=true" : "";
  return apiPlanejamentoTributario.get(`/servicos${query}`);
}

export function buscarServicoPorTipo(tipoPrevia) {
  const tipo = encodeURIComponent(String(tipoPrevia ?? "").trim());
  return apiPlanejamentoTributario.get(`/servicos/tipo/${tipo}`);
}

export function buscarTiposPreviaPorServico(idServico) {
  return apiPlanejamentoTributario.get(`/servicos/${idServico}/tipos`);
}

export function cadastrarAssociadoDetalhado(body) {
  return apiPlanejamentoTributario.post(`/associados`, body);
}

export async function cadastrarClienteDetalhado(body) {
  const retorno = await apiPlanejamentoTributario.post(`/clientes`, body);
  return retorno;
}

export async function buscarClienteSimples(id) {
  return await apiPlanejamentoTributario.get(`/clientes/simples/${id}`);
}

export async function verificarTokenClienteSimplesHandler(token) {
  Token.setToken(token);
  return await apiPlanejamentoTributario.get(`/clientes/simples-por-token`);
}

export async function cadastrarClienteSimples(body) {
  return await apiPlanejamentoTributario.post(`/clientes/simples`, body);
}

export async function buscarObservacoesClientes(idCliente) {
  return await apiPlanejamentoTributario.get(
    `/clientes/simples/observacao/${idCliente}`
  );
}

export async function cadastrarObservacoesClientes(body) {
  return await apiPlanejamentoTributario.post(
    `/clientes/simples/observacao`,
    body
  );
}

export function cadastrarVinculosTiposDeNegocio(tipoCadastro, body) {
  return apiPlanejamentoTributario.post(
    `/cadastrar-tipos-negocio/${tipoCadastro}`,
    body
  );
}

export function cadastrarSugestaoDeServicos(body) {
  return apiPlanejamentoTributario.post(`/clientes/sugestao-de-servicos`, body);
}

export function buscarIdCadastroDetalhado(tipoCadastro) {
  return apiPlanejamentoTributario.get(
    `/buscar-id-cadastro-detalhado/${tipoCadastro}`
  );
}

export function buscarStatus() {
  return apiPlanejamentoTributario.get(`/buscar-status`);
}

export function buscarParceiroPorUsuario() {
  return apiPlanejamentoTributario.get(`/parceiros/buscar-por-usuario`);
}

export function buscarParceirosPorAssociados(idAssociado) {
  return apiPlanejamentoTributario.get(
    `/associados/buscar-parceiro/${idAssociado}`
  );
}

export function buscarDadosParceiro(idUsuario) {
  return apiPlanejamentoTributario.get(`/parceiros/${idUsuario}`);
}

//Buscar Associado por ID
export function buscarAssociadoPorId() {
  return apiPlanejamentoTributario.get(`/associados/buscar-por-usuario`);
}

export function buscarDadosAssociado(idAssociado) {
  return apiPlanejamentoTributario.get(`/associados/${idAssociado}`);
}

export function salvarNegociacao(body) {
  return apiPlanejamentoTributario.post(`/clientes/cadastrar-negociacao`, body);
}

export function buscarClientesPorIdParcAssoc(tipoNegocio, idParcAssoc) {
  return apiPlanejamentoTributario.get(
    `/clientes/${tipoNegocio}/${idParcAssoc}`
  );
}

export function buscarClientesPorTexto(texto) {
  return apiPlanejamentoTributario.get(
    `/clientes/buscar-por-texto?texto=${texto}`
  );
}

export function buscarDadosCliente(idCliente) {
  return apiPlanejamentoTributario.get(`/clientes/${idCliente}`);
}

export function downloadArquivos(id, arquivo, tipoCadastro) {
  return apiPlanejamentoTributario.get(
    `/downloadArquivos?id=${id}&arquivo=${arquivo}&tipo=${tipoCadastro}`
  );
}

export function downloadTodosArquivos(id, tipoCadastro) {
  return apiPlanejamentoTributario.get(
    `/downloadAll?id=${id}&tipoCadastro=${tipoCadastro}`
  );
}

export function buscarUsuarios() {
  return apiPlanejamentoTributario.get(`/buscar-usuarios`);
}

export function buscarUsuariosPorTexto(textoDeBusca) {
  return apiPlanejamentoTributario.get(
    `/buscar-usuarios-texto?textoDeBusca=${textoDeBusca}`
  );
}

export function buscarUsuariosPorIdPai(tipo, idUsuario) {
  return apiPlanejamentoTributario.get(
    `/buscar-usuarios-por-pai?tipo=${tipo}&idUsuario=${idUsuario}`
  );
}

export function aprovarCadastro(body) {
  return apiPlanejamentoTributario.put(`/aprovar`, body);
}

export function reprovarCadastro(body) {
  return apiPlanejamentoTributario.post(`/reprovar`, body);
}

export function buscarDadosPendentes(tipoCadastro, idCandidato) {
  return apiPlanejamentoTributario.get(
    `/buscar-dados-pendentes/${tipoCadastro}/${idCandidato}`
  );
}

export function termoComissionamento(body) {
  return apiPlanejamentoTributario.post(`/termo-comissionamento`, body);
}

export function conviteParaParceiro(body) {
  return apiPlanejamentoTributario.post(`/parceiros/convite`, body);
}

export function inserirCheckpoint(body) {
  return apiPlanejamentoTributario.post(`/checkpoint`, body);
}

export function buscarCheckpoint() {
  return apiPlanejamentoTributario.get(`/checkpoint`);
}

export function buscarHistorico(tipoUsuario, idUsuario) {
  return apiPlanejamentoTributario.get(
    `/historico/${tipoUsuario}/${idUsuario}`
  );
}

export function buscarDetailHistorico(idHistorico) {
  return apiPlanejamentoTributario.get(`/historico/${idHistorico}`);
}

export function downloadAnexoHistorico(idDadoHistorico) {
  return apiPlanejamentoTributario.get(
    `/downloadDadoHistorico/${idDadoHistorico}`
  );
}

export function buscarWorkflow() {
  return apiPlanejamentoTributario.get(`/workflow`);
}

export function buscarWorkflowPorTexto(texto) {
  return apiPlanejamentoTributario.get(
    `/workflow/buscar-por-texto?texto=${texto}`
  );
}

export function buscarVinculosPorWorkflow(workflow) {
  return apiPlanejamentoTributario.get(
    `/workflow/buscar-vinculos-por-processo/${workflow}`
  );
}

export function adicionarVinculosWorkflow(body) {
  return apiPlanejamentoTributario.post(`/workflow/vinculo`, body);
}

export function alterarOrdemVinculosWorkflow(body) {
  return apiPlanejamentoTributario.put(`/workflow/altera-ordem-vinculo`, body);
}

export function buscarSolicitacoesPorStatus(status) {
  return apiPlanejamentoTributario.get(
    `/workflow/buscar-solicitacoes-por-usuario-status/${status}`
  );
}

export function substituirVinculosWorkflow(body) {
  return apiPlanejamentoTributario.put(`/workflow/substitui-vinculo`, body);
}

export function buscarQtdSolicitacaoPorUsuario() {
  return apiPlanejamentoTributario.get(
    `/workflow/buscar-qtd-solicitacoes-por-usuario`
  );
}

export function encurtarFluxoWorkflow(body) {
  return apiPlanejamentoTributario.put(`/workflow/encurtar-fluxo`, body);
}

export function buscarTimelineSolicitacao(id) {
  return apiPlanejamentoTributario.get(
    `/workflow/buscar-timeline-por-solicitacao/${id}`
  );
}

export function inserirSolicitacao(body) {
  return apiPlanejamentoTributario.post(`/workflow/nova-solicitacao`, body);
}

export function verificarPermissaoAprovacao(idSolicitacao) {
  return apiPlanejamentoTributario.get(
    `/verificar-permissao-aprovacao/${idSolicitacao}`
  );
}

export function obterPreCadastro(cnpj) {
  return apiPlanejamentoTributario.get(`/precadastro/${cnpj}`);
}

export function buscaConversa(idInteracao, idUsuario) {
  return apiPlanejamentoTributario.get(
    `/chatAssincrono/${idInteracao}/${idUsuario ? idUsuario : ""}`
  );
}

export function enviaMsg(body) {
  return apiPlanejamentoTributario.post(`/chatAssincrono/`, body);
}

export function buscarCategoriasTributarias() {
  return apiPlanejamentoTributario.get(`/categories`);
}

export function buscaCategoriasTribPorTipoNegocio(tipo) {
  return apiPlanejamentoTributario.get(`/categories/tipo-negocio?tipo=${tipo}`);
}

export function buscarCategoriasTributariasPorTexto(texto) {
  return apiPlanejamentoTributario.get(`/categories/search?text=${texto}`);
}

export function atualizarCategoriasTributarias(id, body) {
  return apiPlanejamentoTributario.put(`/categories/${id}`, body);
}

export function inserirCategoriasTributarias(body) {
  return apiPlanejamentoTributario.post(`/categories`, body);
}

export function deletarCategoriasTributarias(id) {
  return apiPlanejamentoTributario.delete(`/categories/${id}`);
}

export function buscarContasResultado() {
  return apiPlanejamentoTributario.get(`/contas-resultado`);
}

export function buscarContasResultadoPorTexto(texto) {
  const q = encodeURIComponent(texto ?? "");
  return apiPlanejamentoTributario.get(`/contas-resultado/search?text=${q}`);
}

export function inserirContaResultado(body) {
  return apiPlanejamentoTributario.post(`/contas-resultado`, body);
}

export function atualizarContaResultado(id, body) {
  return apiPlanejamentoTributario.put(`/contas-resultado/${id}`, body);
}

export function excluirContaResultado(id) {
  return apiPlanejamentoTributario.delete(`/contas-resultado/${id}`);
}

/** Lista SELIC acumulada (view tributacao_selic_acumulada). */
export function buscarTributacaoSelicAcumulada() {
  return apiPlanejamentoTributario.get(`/tributacao-selic/acumulada`, {
    headers: getAuthHeaders(),
  });
}

/** Executa a function public.atualizar_selic() no banco. */
export function executarAtualizacaoSelic() {
  return apiPlanejamentoTributario.post(`/tributacao-selic/atualizar`, {}, {
    headers: getAuthHeaders(),
  });
}

export function buscarCategoriasVerbas() {
  return apiPlanejamentoTributario.get(`/categorias-verbas/`);
}
export function buscarCategoriaVerbas() {
  return apiPlanejamentoTributario.get(`/categoria-verbas/`);
}

export function buscarCategoriasVerbasPorTexto(texto) {
  return apiPlanejamentoTributario.get(
    `/categorias-verbas/search?text=${texto}`
  );
}

export function atualizarCategoriasVerbas(id, body) {
  return apiPlanejamentoTributario.put(`/categorias-verbas/${id}`, body);
}

export function inserirCategoriasVerbas(body) {
  return apiPlanejamentoTributario.post(`/categorias-verbas`, body);
}

export function deletarCategoriasVerbas(id) {
  return apiPlanejamentoTributario.delete(`/categorias-verbas/${id}`);
}

export function buscarPreviasInss() {
  return apiPlanejamentoTributario.get(`/previas/inss`);
}

// Buscar PDF base64 de uma prévia por id (pode aceitar idPrevia ou idPreviaVersao conforme backend)
export function buscarPdfPrevia(id) {
  // If an object with cnpj is provided, call the CNPJ route
  // Example: GET /previas/inss/buscar-pdf/{cnpj}?idCliente=...
  if (id && typeof id === "object") {
    const { cnpj, idCliente } = id;
    if (!cnpj) throw new Error("cnpj is required");
    const cnpjDigits = String(cnpj).replace(/\D/g, "");
    const query = idCliente
      ? `?idCliente=${encodeURIComponent(String(idCliente))}`
      : "";
    return apiPlanejamentoTributario.get(
      `/previas/inss/buscar-pdf/${encodeURIComponent(cnpjDigits)}${query}`
    );
  }

  // Otherwise assume primitive id (idPrevia or idVersao) and call existing route
  return apiPlanejamentoTributario.get(`/previas/inss/buscar-pdf/${id}`);
}

// Actions on cliente within previas (CNJP-based endpoints)

export function aprovarClientePrevias(cnpj, idPreviaOrBody) {
  if (!cnpj) throw new Error("cnpj is required");
  const cnpjDigits = String(cnpj).replace(/\D/g, "");
  const body =
    typeof idPreviaOrBody === "number"
      ? { idPrevia: idPreviaOrBody }
      : idPreviaOrBody && typeof idPreviaOrBody === "object"
      ? idPreviaOrBody
      : {};
  return apiPlanejamentoTributario.post(
    `/previas/inss/cliente/${encodeURIComponent(cnpjDigits)}/aprovar`,
    body
  );
}

export function reprovarClientePrevias(cnpj, idPreviaOrBody) {
  if (!cnpj) throw new Error("cnpj is required");
  const cnpjDigits = String(cnpj).replace(/\D/g, "");
  const body =
    typeof idPreviaOrBody === "number"
      ? { idPrevia: idPreviaOrBody }
      : idPreviaOrBody && typeof idPreviaOrBody === "object"
      ? idPreviaOrBody
      : {};
  return apiPlanejamentoTributario.post(
    `/previas/inss/cliente/${encodeURIComponent(cnpjDigits)}/reprovar`,
    body
  );
}

export function solicitarAlteracaoClientePrevias(cnpj, body) {
  if (!cnpj) throw new Error("cnpj is required");
  const cnpjDigits = String(cnpj).replace(/\D/g, "");
  return apiPlanejamentoTributario.post(
    `/previas/inss/cliente/${encodeURIComponent(
      cnpjDigits
    )}/solicitar-alteracao`,
    body
  );
}

export function buscarObservacaoTributarioPrevia(cnpj) {
  if (!cnpj) throw new Error("cnpj is required");
  const cnpjDigits = String(cnpj).replace(/\D/g, "");
  return apiPlanejamentoTributario.get(
    `/previas/inss/cliente/${encodeURIComponent(
      cnpjDigits
    )}/tributario/observacao`
  );
}

export function buscarPreviasInssPorTexto(texto) {
  return apiPlanejamentoTributario.get(`/previas/inss/${texto}`);
}

export function editarPreviasInss(id, body) {
  return apiPlanejamentoTributario.put(`/previas/inss/${id}`, body);
}

export function solicitaDocsPrevias(body) {
  return apiPlanejamentoTributario.post(`/previas/previa-doc-pendente`, body);
}

export function trazDatasPrevias(cnpj) {
  return apiPlanejamentoTributario.get(`/previas/traz-datas/${cnpj}`);
}

export function buscarTipoNegocioPorId(id, tipo) {
  return apiPlanejamentoTributario.get(`/buscar-tipos-negocio/${id}/${tipo}`);
}

export function iniciarProjetoInss(idPrevia) {
  return apiPlanejamentoTributario.post(`/projetos/inss/${idPrevia}`);
}

export function iniciarProjetoIcms(idPrevia, idPreviaVersao = null) {
  const body = { idPrevia };
  if (idPreviaVersao) {
    body.idPreviaVersao = idPreviaVersao;
  }
  return apiPlanejamentoTributario.post(`/projetos/icms/iniciar`, body);
}


export function buscarProjetos(tipoProjeto) {
  // tipoProjeto esperado: 'inss' | 'icms' | 'piscofins'
  return apiPlanejamentoTributario.get(`/projetos/${tipoProjeto}`);
}

export function buscarVersoesdaPrevia(tipo, idPrevia) {
  return apiPlanejamentoTributario.get(`/previas/versao/${tipo}/${idPrevia}`);
}

export function buscarVersoesProjeto(tipo, idProjeto) {
  return apiPlanejamentoTributario.get(`/projetos/versao/${tipo}/${idProjeto}`);
}

export function criarVersaoPrevia(body) {
  return apiPlanejamentoTributario.post(`/previas/versao`, body);
}

export function criarVersaoPreviaComMotor(body) {
  return apiPlanejamentoTributario.post(`/previas/versao-com-motor`, body);
}

export function editarVersaoPreviaCompleta(body) {
  return apiPlanejamentoTributario.put(`/previas/versao/editar`, body);
}

export function excluirERecriarVersaoPreviaCompleta(body) {
  return apiPlanejamentoTributario.put(
    `/previas/versao/excluir-e-recriar`,
    body
  );
}

export function criarVersaoProjeto(body) {
  // Determinar qual endpoint usar baseado no tipoPrevia
  if (body.tipoPrevia === "ICMS") {
    return apiPlanejamentoTributario.post(
      `/projetos/versao/piscofins-icms`,
      body
    );
  } else {
    return apiPlanejamentoTributario.post(`/projetos/versao`, body);
  }
}

export function executarMotorAutomatico(idPreviaVersao) {
  return apiPlanejamentoTributario.post(
    `/previas/executar-motor-automatico/${idPreviaVersao}`
  );
}

export function editarVersaoProjeto(body, idVersao) {
  return apiPlanejamentoTributario.put(`/projetos/versao/${idVersao}`, body);
}

export function editarVersaoPrevia(body, idVersao) {
  return apiPlanejamentoTributario.put(`/previas/versao/${idVersao}`, body);
}

export async function gerarRelatorioProjetoInssConsolidado(
  idProjetoVersao,
  params = {}
) {
  const qs = [];
  if (params?.inicio) qs.push(`inicio=${encodeURIComponent(params.inicio)}`);
  if (params?.fim) qs.push(`fim=${encodeURIComponent(params.fim)}`);
  if (params?.tipoItem)
    qs.push(`tipoItem=${encodeURIComponent(params.tipoItem.toLowerCase())}`);
  const query = qs.length > 0 ? `?${qs.join("&")}` : "";
  return await apiPlanejamentoTributario.get(
    `/projetos/inss/gerar-relatorio-consolidado/INSS/${idProjetoVersao}${query}`
  );
}

export function buscarPreviasIcms() {
  return apiPlanejamentoTributario.get(`/previas/icms`);
}

export function buscarPreviasIcmsPorTexto(texto) {
  return apiPlanejamentoTributario.get(`/previas/icms/${texto}`);
}

export function editarPreviasIcms(id, body) {
  return apiPlanejamentoTributario.put(`/previas/icms/${id}`, body);
}


// Funções específicas para criar prévias

export function criarNovaPreviaIcms(body) {
  return apiPlanejamentoTributario.post(`/previas/criar-com-motor`, body);
}

export function criarNovaPreviaInss(body) {
  return apiPlanejamentoTributario.post(`/previas/inss`, body);
}

export const buscarCategoriasVerbas2 = async () => {
  const response = await apiPlanejamentoTributario.get(
    "/verbas/categorias-verbas"
  );
  return response.data;
};

export async function salvarCategoriasDasVerbas(payload) {
  return await apiPlanejamentoTributario.post(`/verbas/categorizar`, payload);
}

export async function buscarVerbasComCategorias(payload) {
  return await apiPlanejamentoTributario.post(
    `/previas/inss/buscar-categorias-por-verbas`,
    payload
  );
}

export async function salvartVinculoCategoriaVerba(payload) {
  return await apiPlanejamentoTributario.post(
    `/previas/inss/inserir-vinculo-categoria-verba`,
    payload
  );
}

export async function gerarRelatorioPreviaInssConsolidado(
  idPrevia,
  params = {}
) {
  const qs = [];
  if (params?.inicio) qs.push(`inicio=${encodeURIComponent(params.inicio)}`);
  if (params?.fim) qs.push(`fim=${encodeURIComponent(params.fim)}`);
  if (params?.tipoItem)
    qs.push(`tipoItem=${encodeURIComponent(params.tipoItem)}`);
  const query = qs.length > 0 ? `?${qs.join("&")}` : "";
  return await apiPlanejamentoTributario.get(
    `/previas/inss/gerar-relatorio-consolidado/INSS/${idPrevia}${query}`
  );
}

export async function enviarPreviaInss(payload) {
  return await apiPlanejamentoTributario.post(
    `/previas/enviar-previa-inss`,
    payload
  );
}

// Novo: grava o PDF base64 da prévia no banco (endpoint adicionado no backend)
export async function gravarPdfPrevia(body) {
  return await apiPlanejamentoTributario.post(`previas/inss/gravar-pdf`, body);
}

export async function criarObservacaoPrevia(body) {
  return await apiPlanejamentoTributario.post(
    `/previas/inss/observacoes`,
    body
  );
}

export async function listarObservacoesPrevias(idPreviaOrCnpj) {
  return await apiPlanejamentoTributario.get(
    `/previas/inss/observacoes/list/${encodeURIComponent(
      String(idPreviaOrCnpj)
    )}`
  );
}

export async function enviarProjetoInss(payload) {
  return await apiPlanejamentoTributario.post(
    `/projetos/enviar-projeto-inss`,
    payload
  );
}


export async function buscarVersaoGeral(idPreviaVersao) {
  return await apiPlanejamentoTributario.get(
    `/previas/buscar-versao-geral/${idPreviaVersao}`
  );
}

export async function buscarVersaoResumoGeral(schema, payload) {
  return await apiPlanejamentoTributario.post(
    `/previas/resumo-geral?schema=${schema}`,
    payload
  );
}

export async function reenviarEmailToken(body) {
  return await apiPlanejamentoTributario.post(
    `/clientes/reenviar-email-token`,
    body
  );
}

export async function atualizarClienteDetalhado(idCliente, body) {
  const retorno = await apiPlanejamentoTributario.put(
    `/clientes/${idCliente}`,
    body
  );
  return retorno;
}


// Funções específicas para ICMS
export async function buscarPreviaVersaoIcms(idVersao) {
  return await apiPlanejamentoTributario.get(
    `/previas/icms/versao/${idVersao}`
  );
}

export function executarMotorAutomaticoIcms(idPreviaVersao) {
  return apiPlanejamentoTributario.post(
    `/previas/executar-motor-automatico/${idPreviaVersao}`
  );
}

export async function enviarPreviaIcms(payload) {
  return await apiPlanejamentoTributario.post(
    `/previas/icms/enviar-previa`,
    payload
  );
}

export async function atualizarPreviaMotorResumo(idPreviaMotorResumo, body) {
  return await apiPlanejamentoTributario.put(
    `/previas/motor-previa/versao/${idPreviaMotorResumo}`,
    body
  );
}

export default apiPlanejamentoTributario;

// ==========================
// PROJETOS - Serviços novos
// ==========================

export async function buscarProjetoVersaoIcms(idVersao) {
  return await apiPlanejamentoTributario.get(
    `/projetos/versao/icms/${idVersao}`
  );
}

export async function atualizarProjetoMotorResumo(idProjetoMotorResumo, body) {
  return await apiPlanejamentoTributario.put(
    `/motor-projeto/resumo/${idProjetoMotorResumo}`,
    body
  );
}

// ==========================
// ENDPOINTS ADICIONAIS PARA PROJETOS
// ==========================

/**
 * Busca versão do projeto com resumos usando motor de projetos
 * @param {number} idVersao - ID da versão
 * @param {string} tipoProjeto - Tipo do projeto (PIS/COFINS, ICMS)
 * @returns {Promise<Object>} Dados completos da versão
 */
export async function buscarProjetoVersaoComResumos(idVersao, tipoProjeto) {
  return await apiPlanejamentoTributario.get(
    `/motor-projeto/versao/${idVersao}?tipo=${tipoProjeto}`
  );
}

/**
 * Copia resumos da prévia para o projeto
 * @param {Object} body - Dados para cópia
 * @returns {Promise<Object>} Resultado da cópia
 */
export async function copiarResumosPreviaParaProjeto(body) {
  return await apiPlanejamentoTributario.post(
    `/motor-projeto/copiar-resumos`,
    body
  );
}

/**
 * Busca lista de bases legais (plan_trib_base_legal)
 * @param {string} [tipo="CONCILIACAO_SPED"] - Agrupamento por apuração (ex.: CONCILIACAO_SPED, ESOCIAL)
 * @returns {Promise<Object>} Lista de bases legais
 */
export async function buscarPlanTribBaseLegal(tipo = "CONCILIACAO_SPED") {
  return await apiPlanejamentoTributario.get(`/base-legal`, { params: { tipo } });
}

/**
 * Lista jobs da fila job_processo_relatorio (api-planejamento-tributario).
 * @param {Record<string, string|number|undefined>} params - id, conta, periodo_busca_de/ate, id_relatorio, status, id_empresa, cnpj, apuracao_id, criado_em_de, criado_em_ate, limit, offset
 */
export async function listarJobsProcessoRelatorio(params = {}) {
  const search = new URLSearchParams();
  const keys = [
    "id",
    "id_relatorio",
    "status",
    "id_empresa",
    "cnpj",
    "apuracao_id",
    "conta",
    "periodo_busca_de",
    "periodo_busca_ate",
    "criado_em_de",
    "criado_em_ate",
    "limit",
    "offset",
  ];
  keys.forEach((k) => {
    const v = params[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") {
      search.set(k, String(v));
    }
  });
  const qs = search.toString();
  const path = qs
    ? `/job-processo-relatorio?${qs}`
    : `/job-processo-relatorio`;
  return await apiPlanejamentoTributario.get(path);
}

/**
 * Busca um job por id (bigserial).
 * @param {string|number} id
 */
export async function buscarJobProcessoRelatorioPorId(id) {
  return await apiPlanejamentoTributario.get(
    `/job-processo-relatorio/${encodeURIComponent(String(id))}`
  );
}

/**
 * @param {string|number} id
 * @param {{ prioridade: number }} body
 */
export async function patchPrioridadeJobProcessoRelatorio(id, body) {
  return await apiPlanejamentoTributario.patch(
    `/job-processo-relatorio/${encodeURIComponent(String(id))}/prioridade`,
    body
  );
}

/** @param {string|number} id */
export async function subirPrioridadeJobProcessoRelatorio(id) {
  return await apiPlanejamentoTributario.patch(
    `/job-processo-relatorio/${encodeURIComponent(String(id))}/prioridade/subir`
  );
}

/** @param {string|number} id */
export async function descerPrioridadeJobProcessoRelatorio(id) {
  return await apiPlanejamentoTributario.patch(
    `/job-processo-relatorio/${encodeURIComponent(String(id))}/prioridade/descer`
  );
}

/** @param {string|number} id */
export async function postReexecutarJobProcessoRelatorio(id) {
  return await apiPlanejamentoTributario.post(
    `/job-processo-relatorio/${encodeURIComponent(String(id))}/reexecutar`
  );
}

/* ----------------------------- TXM-594 — Tabela NCM ----------------------------- */

/** Filtros combináveis + paginação server-side. Só envia o que foi preenchido. */
export async function buscarTabelaNcm(filtros = {}) {
  const params = new URLSearchParams();
  Object.entries(filtros).forEach(([chave, valor]) => {
    if (valor !== undefined && valor !== null && valor !== "") {
      params.append(chave, String(valor));
    }
  });
  const query = params.toString();
  return await apiPlanejamentoTributario.get(
    `/tabela-ncm${query ? `?${query}` : ""}`
  );
}

export async function inserirItemTabelaNcm(body) {
  return await apiPlanejamentoTributario.post(`/tabela-ncm`, body);
}

export async function atualizarItemTabelaNcm(id, body) {
  return await apiPlanejamentoTributario.put(
    `/tabela-ncm/${encodeURIComponent(String(id))}`,
    body
  );
}

/** Linhas já parseadas no cliente — o backend é agnóstico ao formato do arquivo. */
export async function importarLoteTabelaNcm(body) {
  return await apiPlanejamentoTributario.post(`/tabela-ncm/importar`, body);
}

export async function copiarBaseTabelaNcm(body) {
  return await apiPlanejamentoTributario.post(`/tabela-ncm/copiar`, body);
}

/** CNPJs com pelo menos um item na Tabela NCM (filtro da listagem); sem grupo = todos. */
export async function listarCnpjsCadastradosTabelaNcm(idGrupoEconomico) {
  const query = idGrupoEconomico
    ? `?id_grupo_economico=${encodeURIComponent(String(idGrupoEconomico))}`
    : "";
  return await apiPlanejamentoTributario.get(`/tabela-ncm/cnpjs${query}`);
}

/** CNPJs do grupo (sem repetição), cada um com razão social e a UF cadastrada (ou null). */
export async function listarEmpresasTabelaNcm(idGrupoEconomico) {
  return await apiPlanejamentoTributario.get(
    `/tabela-ncm/empresas?id_grupo_economico=${encodeURIComponent(String(idGrupoEconomico))}`
  );
}
